<script setup lang="ts">
import PanelGroup from '../../PanelGroup.vue'
import MissionEditor from './MissionEditor.vue'

import { VueDraggableNext as draggable } from 'vue-draggable-next'
import { ArrowUpRight, GripVertical, Loader2, Pencil, Plus, Trash2, Wallet } from 'lucide-vue-next'

import { MISSION_HEROICONS } from '../../missionIcons'
import { renderMissionText } from '../../missionText'
import useMissionInstances from '../../../composables/missionInstances'
import { useEditorLoyaltyData } from '../../../composables/editorLoyaltyData'
import useContentEditing from '../../../composables/useContentEditing'
import { canShowMission, isMissionShown, isWalletMissionLocked, missionKey } from '../../../lib/brandAppMissions'

import type { WebappMission } from '@/types/types'

const { locale, t } = useI18n()
const { user, hasWallet } = useLocalStorage()
const { missions, isLoadingMissions, missionsError, reloadMissions, pointsLabel } = useEditorLoyaltyData()
const { fetchInstances, instancesFor, errorFor, needsFetch, isLoading: isLoadingInstances } = useMissionInstances()

const { editing, startEditing, stopEditing } = useContentEditing()
const editingIndex = ref<number | null>(null)
const newMission = ref<WebappMission | null>(null)
const editorOpen = computed(() => editing.value?.kind === 'mission')
const editingMission = computed(() =>
  editorOpen.value
    ? (newMission.value ?? (editingIndex.value === null ? null : missions.value[editingIndex.value]))
    : null
)
const editingKey = computed(() =>
  newMission.value || editingIndex.value === null
    ? 'new'
    : missionKey(missions.value[editingIndex.value], editingIndex.value)
)

const rows = computed({
  get: () => missions.value,
  set: (next: WebappMission[]) => {
    missions.value = next
  }
})

const setMissionVisible = (index: number, isVisible: boolean) => {
  const target = missions.value[index]
  if (isVisible && !canShowMission(target, render(target, 'title'))) {
    openEditor(index)
    return
  }
  missions.value = missions.value.map((mission, position) => (position === index ? { ...mission, isVisible } : mission))
}

const isCustom = (mission: WebappMission) => mission.type === null

const removeMission = (index: number) => {
  missions.value = missions.value.filter((_, position) => position !== index)
}

const isLocked = (mission: WebappMission) => isWalletMissionLocked(mission, hasWallet.value)
const isShown = (mission: WebappMission) => isMissionShown(mission, hasWallet.value)
const hasLockedWallet = computed(() => missions.value.some(isLocked))

const visibleCount = computed(() => missions.value.filter(isShown).length)

const render = (mission: WebappMission, field: 'title' | 'subtitle') =>
  renderMissionText(mission[field], locale.value, {
    points: mission.points,
    label: pointsLabel.value,
    merchantName: user.value.merchant.name ?? ''
  })

const openEditor = (index: number) => {
  newMission.value = null
  editingIndex.value = index
  const type = missions.value[index].type
  fetchInstances(needsFetch(type) ? type : false)
  startEditing('mission', false)
}

const addMission = () => {
  editingIndex.value = null
  newMission.value = {
    id: null,
    type: null,
    title: { [locale.value]: '' },
    subtitle: {},
    ctaLabel: {},
    iconType: 'emoji',
    iconValue: '🎯',
    points: 0,
    destinationUrl: '',
    instanceType: null,
    instanceId: null,
    isVisible: true,
    order: missions.value.length
  }
  startEditing('mission', true)
}

const closeEditor = () => {
  stopEditing('mission')
  editingIndex.value = null
  newMission.value = null
}

const save = (mission: WebappMission) => {
  if (newMission.value) missions.value = [...missions.value, mission]
  else if (editingIndex.value !== null) {
    const next = [...missions.value]
    next[editingIndex.value] = mission
    missions.value = next
  }
  closeEditor()
}

onBeforeUnmount(() => stopEditing('mission'))

onMounted(() => fetchInstances())
</script>

<template>
  <MissionEditor
    v-if="editingMission"
    :key="editingKey"
    class="motion-safe:animate-panel-forward"
    :mission="editingMission"
    :mission-key="editingKey"
    :instances="instancesFor(editingMission.type)"
    :is-loading-instances="isLoadingInstances"
    :instances-error="errorFor(editingMission.type)"
    @save="save"
    @close="closeEditor"
    @refresh-instances="fetchInstances(editingMission?.type ?? true)"
  />

  <PanelGroup
    v-else
    :title="t('pages.loyaltySupports.webapp.missions.listTitle')"
    :count="`${visibleCount} / ${missions.length}`"
  >
    <div class="flex flex-col gap-4">
      <p class="text-muted-foreground text-xs leading-snug">
        {{ t('pages.loyaltySupports.webapp.editor.loyalty.missionsIntro') }}
      </p>

      <BaseTip v-if="hasLockedWallet" severity="warn" class="items-center [&>svg]:translate-y-0">
        <template #icon>
          <Wallet :stroke-width="2.5" />
        </template>
        <template #text>
          {{ t('pages.loyaltySupports.webapp.editor.loyalty.walletLocked') }}
        </template>
        <template #button>
          <Button variant="outline" size="sm" as-child>
            <RouterLink :to="{ name: 'Wallet' }" target="_blank" rel="noopener">
              {{ t('pages.loyaltySupports.webapp.editor.loyalty.walletSetup') }}
              <ArrowUpRight />
            </RouterLink>
          </Button>
        </template>
      </BaseTip>

      <div v-if="isLoadingMissions" role="status" class="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 class="size-4 animate-spin" />{{ t('pages.loyaltySupports.webapp.editor.loyalty.missionsLoading') }}
      </div>

      <div v-else-if="missionsError" class="flex flex-col items-start gap-2">
        <p role="alert" class="text-destructive text-sm">
          {{ t('pages.loyaltySupports.webapp.editor.loyalty.missionsLoadFailed') }}
        </p>
        <Button variant="outline" size="sm" @click="reloadMissions">{{
          t('pages.loyaltySupports.webapp.editor.loyalty.retry')
        }}</Button>
      </div>

      <p v-else-if="!missions.length" class="text-muted-foreground text-sm">
        {{ t('pages.loyaltySupports.webapp.editor.loyalty.noMissions') }}
      </p>

      <draggable v-else v-model="rows" handle=".drag-handle" :animation="200" class="flex flex-col gap-2">
        <div
          v-for="(mission, index) in rows"
          :key="missionKey(mission, index)"
          class="border-border bg-background shadow-surface flex items-center gap-3 rounded-xl border p-3"
        >
          <GripVertical class="drag-handle text-foreground-disabled size-4 shrink-0 cursor-grab" />
          <span
            class="bg-muted flex size-10 shrink-0 items-center justify-center rounded-xl transition-opacity"
            :class="isShown(mission) ? '' : 'opacity-50'"
          >
            <component
              :is="MISSION_HEROICONS[mission.iconValue] ?? MISSION_HEROICONS.SparklesIcon"
              v-if="mission.iconType === 'heroicon'"
              class="text-primary size-4.5"
              aria-hidden="true"
            />
            <span v-else class="text-lg leading-none" aria-hidden="true">{{ mission.iconValue }}</span>
          </span>
          <span class="flex min-w-0 flex-col transition-opacity" :class="isShown(mission) ? '' : 'opacity-50'">
            <span class="text-foreground truncate text-sm font-medium">{{ render(mission, 'title') }}</span>
            <span class="text-muted-foreground truncate text-xs">
              {{ mission.points > 0 ? `+${mission.points} ${pointsLabel}` : render(mission, 'subtitle') }}
            </span>
          </span>
          <span class="ml-auto flex shrink-0 items-center gap-1">
            <Button variant="ghost" size="icon-sm" :aria-label="t('global.words.modify')" @click="openEditor(index)">
              <Pencil />
            </Button>
            <Button
              v-if="isCustom(mission)"
              variant="ghost"
              size="icon-sm"
              class="hover:text-destructive"
              :aria-label="t('pages.loyaltySupports.webapp.editor.loyalty.deleteMission')"
              @click="removeMission(index)"
            >
              <Trash2 />
            </Button>
            <Switch
              :model-value="isShown(mission)"
              :disabled="isLocked(mission)"
              :aria-label="t('pages.loyaltySupports.webapp.editor.loyalty.showInApp')"
              @update:model-value="setMissionVisible(index, $event)"
            />
          </span>
        </div>
      </draggable>

      <Button v-if="!isLoadingMissions && !missionsError" variant="outline" class="w-full" @click="addMission">
        <Plus />
        {{ t('pages.loyaltySupports.webapp.editor.loyalty.newMission') }}
      </Button>
    </div>
  </PanelGroup>
</template>
